// The people at the table: each seat with its player, their two cards while they are in a bet,
// their chips pushed toward the board, and for a moment the pot they took or the bust.

import type { Player } from "../../play/room";
import { cash, chips, multiple, won } from "../../play/tape";

const DENOMS = [500n, 100n, 25n, 10n] as const;

/** The chips an amount is made of, biggest first, at most `cap`. `unit` is one dollar. */
export function chipsOf(amount: bigint, unit: bigint, cap = 5): string[] {
  const out: string[] = [];
  let left = amount;
  for (const denom of DENOMS) {
    while (left >= denom * unit && out.length < cap) {
      out.push(denom.toString());
      left -= denom * unit;
    }
  }
  if (left > 0n && out.length < cap) out.push("10");
  return out;
}

/** A stack of chips with what it adds up to. */
export function Stack({ amount, unit, label, ghost = false }: { amount: bigint; unit: bigint; label?: string; ghost?: boolean }) {
  const chips = chipsOf(amount, unit).reverse();
  return (
    <span className={`stack ${ghost ? "is-ghost" : ""}`}>
      <span className="stack-chips" aria-hidden>
        {chips.map((chip, index) => (
          <span key={index} className={`stack-chip pchip-${chip}`} style={{ ["--i" as string]: index }} />
        ))}
      </span>
      {label && <span className="stack-label num">{label}</span>}
    </span>
  );
}

export function Cards({ size = "sm" }: { size?: "sm" | "lg" }) {
  return (
    <span className={`cards cards-${size}`} aria-hidden>
      <span className="card" />
      <span className="card" />
    </span>
  );
}

/** A colour from an address, so a player is the same face every time. */
export const hue = (wallet: string) => {
  let h = 0;
  for (let i = 2; i < 10; i += 1) h = (h * 31 + wallet.charCodeAt(i)) % 360;
  return h;
};

export function Seat({ player, at }: { player: Player | null; at: string }) {
  if (!player) {
    return (
      <div className={`seat seat-${at} is-empty`} aria-hidden>
        <span className="seat-avatar" />
      </div>
    );
  }
  const result = player.result;
  const inHand = player.stake > 0n;
  const side = player.isLong ? "up" : "down";
  const tone = result ? (result.bust ? "is-bust" : won(result.payout, result.stake) ? "is-win" : "is-push") : "";
  return (
    <div className={`seat seat-${at} ${tone}`}>
      <span className={`seat-avatar ring-${side}`} style={{ ["--h" as string]: hue(player.wallet) }}>
        {inHand && <Cards />}
      </span>
      <span className="seat-plate num">
        {result ? (
          result.bust ? "Bust" : multiple(result.payout, result.stake)
        ) : (
          <>
            <span className={side} aria-label={player.isLong ? "Up" : "Down"}>
              {player.isLong ? "▲" : "▼"}
            </span>{" "}
            {chips(player.stake)}
          </>
        )}
      </span>
      {inHand && (
        <span className="seat-bet" aria-hidden>
          <Stack amount={player.stake} unit={10n ** 8n} />
        </span>
      )}
      {result && !result.bust && won(result.payout, result.stake) && <span className="seat-take num">+{cash(result.payout - result.stake, 5)}</span>}
    </div>
  );
}
